import Phaser from "phaser";

/**
 * Andrew's character animation (real art). Simpler than Dan's: Andrew auto-runs
 * to the right and owns jump + throw, so there is no facing flip and no separate
 * standing/running variants. The atlas (idle / run / jump / throw) is packed by
 * `node tools/pack-character.mjs andrew` from the raw sheets in ~/Downloads/andrew.
 *
 * Frame counts are read from the loaded atlas at register time, so re-packing with
 * different windows needs no code change here.
 */
export const ANDREW_ATLAS = "char-andrew";

// Shared playback speed. Idle is a one-shot (used for the pre-run countdown, see
// the scene); run loops; jump and throw play once and hold their last frame.
const FPS = 15;

// Throw playback rate (the clip also gates the throw pose window, so this rate sets
// how quickly he can throw again).
const THROW_FPS = 24;

// Hitbox in frame px. Atlas is packed at CONTENT_H=192 (3x the old 64), so these are
// 3x their original values (22x46, cx 21) and the sprite renders at ANDREW_SCALE/ANDREW_ART.
const BODY_W = 66;
const BODY_H = 138;

// Andrew's running body sits at frame-x ~21, LEFT of the 63px frame's center
// (31.5): the frame is widened on the right by the throw arm's reach. The hitbox
// centers on his body (this), while the render origin stays at frame-center so the
// throw.html-authored ball offsets (which assume origin x 31.5) still line up.
const BODY_CX = 63;

// On-screen scale of the 64px-tall packed art. One knob for Andrew's size, sized
// to give him real presence (near Dan's height). The power-up burst is scaled to
// match him (see POWERUP_H in the scene), so the two stay consistent.
// On-screen size factor (world-space): throw offsets and the power-up burst multiply
// by this. Art is packed at 3x (CONTENT_H 192), so the sprite renders at
// ANDREW_SCALE/ANDREW_ART (crisp, near 1:1) while offsets keep the full ANDREW_SCALE.
export const ANDREW_SCALE = 3.0;
export const ANDREW_ART = 3; // atlas pack multiple vs the original 64px

export const AA = {
  idle: "andrew-idle",
  run: "andrew-run",
  jump: "andrew-jump",
  throw: "andrew-throw",
};

/** Seat the art so the feet plant at the frame bottom while the hitbox stays put
 *  (origin + body offset move together). */
function seat(player: Phaser.Physics.Arcade.Sprite) {
  const nudge = 1;
  player.setOrigin(0.5, 0.5 - nudge / player.height); // frame-center (throw.html aligns to this)
  const body = player.body as Phaser.Physics.Arcade.Body;
  body.setOffset(BODY_CX - BODY_W / 2, player.height - BODY_H - nudge); // center on his body, not the throw-widened frame
}

/** Register Andrew's animations once on the game-global animation manager. */
export function registerAndrewAnims(scene: Phaser.Scene) {
  const anims = scene.anims;
  if (anims.exists(AA.run)) return;

  const names = scene.textures.get(ANDREW_ATLAS).getFrameNames();
  const count = (state: string) =>
    names.filter((n) => new RegExp(`^andrew-${state}-\\d+$`).test(n)).length;

  const make = (key: string, state: string, repeat: number, rate = FPS) => {
    const n = count(state);
    if (n > 0)
      anims.create({
        key,
        frames: anims.generateFrameNames(ANDREW_ATLAS, { prefix: `andrew-${state}-`, start: 0, end: n - 1 }),
        frameRate: rate,
        repeat,
      });
  };

  make(AA.idle, "idle", 0); // one-shot (drives the 3-2-1 countdown)
  make(AA.run, "run", -1); // looping cycle
  make(AA.jump, "jump", 0); // one-shot, holds the last (falling) frame
  make(AA.throw, "throw", 0, THROW_FPS); // one-shot, fast so he can throw again quickly
}

/** Point a freshly-created player sprite at Andrew's atlas + hitbox. */
export function useAndrewSprite(player: Phaser.Physics.Arcade.Sprite) {
  player.setTexture(ANDREW_ATLAS, "andrew-idle-0");
  player.setScale(ANDREW_SCALE / ANDREW_ART); // art packed 3x, render near 1:1 (crisp)
  const body = player.body as Phaser.Physics.Arcade.Body;
  body.setSize(BODY_W, BODY_H);
  seat(player);
}

/** Milliseconds a one-shot Andrew clip runs (from the registered anim), for timing
 *  the throw pose window and the countdown. Valid after registerAndrewAnims(). */
export function andrewAnimMs(scene: Phaser.Scene, key: string): number {
  return scene.anims.get(key)?.duration ?? 0;
}

// Per-frame placement of the thrown token during the throw clip, authored in
// public/throw.html and exported for Andrew. Offsets are from the sprite origin,
// in frame-local px: worldX = x + ox*ANDREW_SCALE, worldY = y + oy*ANDREW_SCALE
// (y down). Andrew always faces right, so there is no facing flip. `front` draws
// the token over Andrew, else behind. The token is held through the windup and
// launches at `release`.
export interface AndrewThrowBall {
  ox: number; oy: number; front: boolean; released: boolean; launchOx: number; launchOy: number;
}
// Authored in public/throw.html against origin (77.5, 95) of the 155x192 frame,
// which matches Andrew's seated sprite origin, so ox/oy apply directly.
const ANDREW_THROW = {
  release: 5,
  offsets: [
    { ox: -8, oy: 15, front: false },
    { ox: -55, oy: 34, front: false },
    { ox: -72, oy: -31, front: true },
    { ox: -65, oy: -46, front: true },
    { ox: 58, oy: -68, front: false },
    { ox: 78, oy: -30, front: false },
    { ox: 78, oy: -30, front: false },
    { ox: 78, oy: -30, front: false },
    { ox: 78, oy: -30, front: false },
  ],
};

/** The thrown token's placement for the throw frame Andrew is currently showing,
 *  or null when he is not mid-throw. While `released` is false, hold the token at
 *  (ox, oy); once true, launch the projectile from (launchOx, launchOy) - the last
 *  in-hand spot (the frame before release). */
export function andrewThrowBall(player: Phaser.Physics.Arcade.Sprite): AndrewThrowBall | null {
  if (player.anims.currentAnim?.key !== AA.throw) return null;
  const offs = ANDREW_THROW.offsets;
  const m = (player.frame?.name ?? "").match(/-(\d+)$/);
  const f = m ? Math.min(+m[1], offs.length - 1) : 0;
  const o = offs[f];
  const li = Math.max(0, Math.min(offs.length - 1, ANDREW_THROW.release - 1));
  const lo = offs[li];
  return { ox: o.ox, oy: o.oy, front: o.front, released: f >= ANDREW_THROW.release, launchOx: lo.ox, launchOy: lo.oy };
}
